/**
 * AIF-03 — confidence-threshold control logic (§4 / story D1).
 *
 * The wizard exposes the floor as an integer PERCENT (1–100) on both a slider
 * and a numeric field. The floor is LOWER-BOUNDED at the file minimum: the
 * incoming files were already pre-thresholded by the inference run, so letting
 * the user drop below the file min is meaningless (it would never keep more
 * detections). Typing a lower value shows an info hint and CLAMPS to the file
 * min rather than silently accepting it.
 *
 * The wire value (`confidenceFloor`) is the 0..1 fraction; the UI value is the
 * 1..100 percent. These helpers convert + clamp between them.
 */

/** Convert a 0..1 wire fraction to an integer percent (1..100). */
export function fractionToPercent(fraction: number): number {
  return Math.round(fraction * 100);
}

/** Convert an integer percent (1..100) to the 0..1 wire fraction. */
export function percentToFraction(percent: number): number {
  return percent / 100;
}

/**
 * The slider/field lower bound as a percent. `fileMin` is the 0..1 file
 * minimum from the AnalyzeReport (null when there were no detections). The
 * bound floors to 1% — never 0 — so the control always has a usable range.
 */
export function floorPercent(fileMin: number | null): number {
  if (fileMin === null) return 1;
  return Math.max(1, fractionToPercent(fileMin));
}

export interface ClampResult {
  /** The clamped percent the control should display. */
  value: number;
  /** True when the requested value was below the file min and got clamped. */
  clamped: boolean;
}

/**
 * Clamp a requested percent into `[floorPercent(fileMin), 100]`. Returns
 * whether a lower-bound clamp happened so the caller can surface the info hint.
 */
export function clampPercent(
  requested: number,
  fileMin: number | null,
): ClampResult {
  const min = floorPercent(fileMin);
  if (!Number.isFinite(requested)) {
    return { value: min, clamped: false };
  }
  const rounded = Math.round(requested);
  if (rounded < min) return { value: min, clamped: true };
  if (rounded > 100) return { value: 100, clamped: false };
  return { value: rounded, clamped: false };
}
